import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAgentSetup } from "./setup-store";
import { loadSetup } from "./setup-api";

// The tenant's own Support Agent widget, live on every signed-in page once
// it's turned on (widget-integration.md). Uses the backend's widget.js;
// public widget calls only work from an allowed origin, so it shows only
// when this app's address is on the setup's allowed websites. The public
// key comes from POST .../widget-installation, which needs an admin, so
// other users won't see it.

type WidgetInstance = { ready: Promise<unknown>; open: () => void; destroy: () => void };

declare global {
  interface Window {
    ConvoHubWidget?: { mount: (o: { key: string; apiBase: string }) => WidgetInstance };
  }
}

const loadWidgetScript = (src: string) =>
  new Promise<void>((resolve, reject) => {
    if (window.ConvoHubWidget) return resolve();
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("load failed"));
    document.body.appendChild(el);
  });

let loaded = false;

const SupportWidget = () => {
  const s = useAgentSetup();
  const location = useLocation();
  const signedIn = !!localStorage.getItem("authToken");
  const instanceRef = useRef<WidgetInstance | null>(null);

  // Load the setup once per app session (the Setup page refreshes it too).
  useEffect(() => {
    if (!signedIn || loaded) return;
    loaded = true;
    loadSetup();
  }, [signedIn, location.pathname]);

  const allowed = s.domains.some((d) => d.domain === window.location.origin);
  const show = signedIn && s.widgetEnabled && !!s.widgetKey && !!s.scriptUrl && allowed;

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    loadWidgetScript(s.scriptUrl)
      .then(() => {
        if (cancelled || !window.ConvoHubWidget) return;
        instanceRef.current = window.ConvoHubWidget.mount({ key: s.widgetKey, apiBase: new URL(".", s.scriptUrl).href.replace(/\/$/, "") });
      })
      .catch(() => {
        /* widget.js unreachable — the app works without it */
      });
    return () => {
      cancelled = true;
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [show, s.widgetKey, s.scriptUrl]);

  return null;
};

// Opens the live widget, e.g. from Setup's Test Agent step.
export const openSupportWidget = () => {
  const host = document.querySelector("[data-convohub-widget]");
  const launcher = host?.shadowRoot?.querySelector<HTMLButtonElement>(".launcher");
  const panel = host?.shadowRoot?.querySelector(".panel");
  if (launcher && panel?.hasAttribute("hidden")) launcher.click();
  return !!launcher;
};

export default SupportWidget;
